import { ArrowRight, BedDouble, Building2, CalendarDays, Car, KeyRound, MapPin, Snowflake, Sofa, Sparkles, WashingMachine, X } from "lucide-react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { type PointerEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApartmentCard } from "../components/ApartmentCard";
import { BookingSearch } from "../components/BookingSearch";
import { HeritageOrnament } from "../components/HeritageOrnament";
import { SEO } from "../components/SEO";
import { HeroParallax } from "../components/motion/HeroParallax";
import { Reveal } from "../components/motion/Reveal";
import { RevealImage } from "../components/motion/RevealImage";
import { Stagger, StaggerItem } from "../components/motion/Stagger";
import { easeOut, heroContainer, heroItem } from "../components/motion/tokens";
import { bookableApartments, getApartment } from "../data/apartments";
import { buildingDescription, siteHighlights, siteInfo } from "../data/site";

const buildingParagraphs = buildingDescription.split(/\n\n+/).map((para) => para.trim()).filter(Boolean);
const gallerySpecs = [
  { value: "≈20", label: "Windows of north light" },
  { value: "24 ft", label: "Ridge-to-floor height" },
  { value: "4", label: "Original timber trusses" },
];

const iconMap = [Building2, Car, Sofa, WashingMachine, KeyRound, Snowflake];
const catalogueImage = "/assets/site/deposit-seed-catalogue-restored-composed-4x.jpg";
// Two plates of the same frontal shot. The wide original carries the whole
// scene and is what the phone hero needs (it scales the full facade into the
// upper band); from 1021px the landscape hero uses a crop pulled in close on
// the stonework, where the establishing shot read as too distant.
const homeHeroImage = "/assets/site/only-for-the-hero.webp";
const homeHeroImageWide = "/assets/site/hero-building-wide.webp";
const heroStats = [
  { value: "19 Center", label: "Restored 1915 factory" },
  { value: `${bookableApartments.length} stays`, label: "Studio to three-bedroom" },
  { value: "On the Delaware", label: "World-class fly fishing" },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function Home() {
  const featured = bookableApartments.slice(0, 6);
  const gallery = getApartment("ag");
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!catalogueOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCatalogueOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [catalogueOpen]);

  const updateCatalogueMagnifier = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === "touch") {
      return;
    }

    const surface = event.currentTarget;
    const image = surface.querySelector("img");
    const lens = surface.querySelector<HTMLElement>(".catalogue-magnifier");

    if (!image || !lens) {
      return;
    }

    surface.classList.add("catalogue-magnifier-active");

    const surfaceRect = surface.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    const lensSize = lens.offsetWidth;
    const zoom = Number.parseFloat(getComputedStyle(surface).getPropertyValue("--lens-zoom")) || 3;
    const imageX = clamp(event.clientX - imageRect.left, 0, imageRect.width);
    const imageY = clamp(event.clientY - imageRect.top, 0, imageRect.height);
    const surfaceX = imageRect.left - surfaceRect.left + imageX;
    const surfaceY = imageRect.top - surfaceRect.top + imageY;
    const backgroundX = lensSize / 2 - imageX * zoom;
    const backgroundY = lensSize / 2 - imageY * zoom;

    surface.style.setProperty("--lens-x", `${surfaceX}px`);
    surface.style.setProperty("--lens-y", `${surfaceY}px`);
    surface.style.setProperty("--lens-bg-x", `${backgroundX}px`);
    surface.style.setProperty("--lens-bg-y", `${backgroundY}px`);
    surface.style.setProperty("--lens-bg-size", `${imageRect.width * zoom}px ${imageRect.height * zoom}px`);
  };

  const hideCatalogueMagnifier = (event: PointerEvent<HTMLElement>) => {
    event.currentTarget.classList.remove("catalogue-magnifier-active");
  };

  return (
    <>
      <SEO
        title="The Deposit Seed House | Furnished Apartments in Deposit, NY"
        description="The Deposit Seed House, located at 19 Center Street is nestled within the small hamlet of Deposit, NY."
        image={siteInfo.heroImage}
        canonicalPath="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          name: siteInfo.name,
          telephone: siteInfo.phone,
          address: siteInfo.address,
          url: "https://thedepositseedhouse.com/",
          image: `https://thedepositseedhouse.com${siteInfo.heroImage}`,
        }}
      />
      <HeroParallax
        className="hero home-hero"
        image={homeHeroImage}
        imageWide={homeHeroImageWide}
        overlay="linear-gradient(102deg, rgba(8,18,16,0.9) 0%, rgba(8,18,16,0.56) 44%, rgba(12,23,21,0.3) 100%)"
      >
        <m.div
          className="home-hero__inner"
          variants={heroContainer}
          initial={reduce ? false : "hidden"}
          animate="visible"
        >
          <m.div className="hero__content" variants={heroContainer}>
            <m.p className="eyebrow" variants={heroItem}>
              Deposit, New York · Est. 1915
            </m.p>
            <m.h1 variants={heroItem}>{siteInfo.name}</m.h1>
            <m.p className="hero__deck" variants={heroItem}>
              {siteInfo.tagline}
            </m.p>
            <m.div className="hero-signal-row" variants={heroItem} aria-label="Property highlights">
              <span>
                <MapPin size={16} aria-hidden="true" />
                Walkable village stay
              </span>
              <span>
                <BedDouble size={16} aria-hidden="true" />
                Turnkey apartments
              </span>
              <span>
                <Sparkles size={16} aria-hidden="true" />
                Restored heritage building
              </span>
            </m.div>
            <m.div variants={heroItem}>
              <HeritageOrnament className="hero__ornament" />
            </m.div>
            <m.div className="hero__actions" variants={heroItem}>
              <Link className="button button--primary" to="/apartments">
                View apartments <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <a className="button button--light" href={siteInfo.youtubeUrl} target="_blank" rel="noreferrer">
                Video tour
              </a>
            </m.div>
            <m.div className="hero-stat-grid" variants={heroItem} aria-label="Property summary">
              {heroStats.map((stat) => (
                <span key={stat.label}>
                  <strong>{stat.value}</strong>
                  <small>{stat.label}</small>
                </span>
              ))}
            </m.div>
          </m.div>
          <m.div className="home-hero__booking" variants={heroItem}>
            <div className="home-hero__booking-heading">
              <span>
                <CalendarDays size={16} aria-hidden="true" />
                Check availability
              </span>
              <small>No payment collected online</small>
            </div>
            <BookingSearch id="availability" compact />
          </m.div>
        </m.div>
        <div className="hero__scroll-cue" aria-hidden="true">
          <span>Explore</span>
          <span className="hero__scroll-track" />
        </div>
      </HeroParallax>
      <section className="section section--intro section--welcome">
        <Reveal className="welcome-layout">
          <div className="welcome-media">
            <RevealImage className="intro-building-image" src={siteInfo.exteriorImage} alt="The Deposit Seed House building" />
            <button className="catalogue-trigger" type="button" onClick={() => setCatalogueOpen(true)} aria-label="Open 1916 Deposit Seed Co. catalogue image">
              <RevealImage className="intro-catalogue-image" src={catalogueImage} alt="1916 Deposit Seed Co. seed catalogue cover" />
            </button>
            <span className="catalogue-caption serif-italic">Original Deposit Seed Co. catalogue, 1916 — restored.</span>
          </div>
          <div className="prose welcome-copy">
            <p className="eyebrow">About the building</p>
            <h2>Welcome to Deposit</h2>
            <HeritageOrnament className="section-ornament" />
            {buildingParagraphs.map((para, index) =>
              index === 0 ? (
                <p key={index} className="building-copy">
                  {para}
                </p>
              ) : (
                <p key={index} className="welcome-body">
                  {para}
                </p>
              ),
            )}
          </div>
        </Reveal>
      </section>
      <section className="section section--muted section--residences">
        <Reveal className="section-heading">
          <p className="eyebrow">Available apartments</p>
          <h2>Furnished units with the essentials already handled.</h2>
          <Link className="text-link" to="/apartments">
            See all apartments <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </Reveal>
        <Stagger className="card-grid card-grid--residences">
          {featured.map((apartment) => (
            <ApartmentCard key={apartment.slug} apartment={apartment} motionItem />
          ))}
        </Stagger>
      </section>
      <section className="section section--collection">
        <Reveal className="collection-layout">
          <div className="collection-media">
            <div className="frame--gilt">
              <RevealImage src="/assets/site/hall-art.jpg" alt="Paintings and prints in the restored hallways of The Deposit Seed House" />
            </div>
          </div>
          <div className="prose collection-copy">
            <p className="eyebrow">The Collection</p>
            <h2>A gallery in the hallways.</h2>
            <HeritageOrnament className="section-ornament" />
            <p>
              Throughout the restored halls hangs a rotating collection of paintings and prints — part of what makes a stay
              here feel like more than a room. The original factory pine floors and block walls remain, a quiet record of the
              building&apos;s 1915 beginnings.
            </p>
            <p className="collection-caption serif-italic">Works on view change through the seasons.</p>
          </div>
        </Reveal>
      </section>
      {gallery && (
        <HeroParallax
          className="gallery-feature"
          image={gallery.featuredImage}
          overlay="linear-gradient(98deg, rgba(7,16,14,0.94) 0%, rgba(7,16,14,0.72) 40%, rgba(7,16,14,0.26) 72%, rgba(7,16,14,0.5) 100%), linear-gradient(180deg, rgba(7,16,14,0.18), rgba(7,16,14,0.66))"
        >
          <Reveal className="gallery-feature__panel">
            <p className="eyebrow">The Gallery · Unit 3A</p>
            <h2>Light beneath the trusses.</h2>
            <span className="rule-gold gallery-feature__rule" aria-hidden="true" />
            <p className="gallery-feature__deck serif-italic">{gallery.summary}</p>
            <dl className="spec-ledger" aria-label="Gallery specifications">
              {gallerySpecs.map((spec) => (
                <div key={spec.label}>
                  <dt>{spec.value}</dt>
                  <dd>{spec.label}</dd>
                </div>
              ))}
            </dl>
            <div className="gallery-feature__actions">
              <Link className="button button--primary" to={`/apartments/${gallery.slug}`}>
                {gallery.listingActionLabel} <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link className="text-link text-link--light" to="/contact">
                {gallery.reserveLabel}
              </Link>
            </div>
          </Reveal>
        </HeroParallax>
      )}
      <section className="section section--features">
        <Reveal className="section-heading section-heading--center">
          <p className="eyebrow">Amenities</p>
          <h2>Everything a comfortable stay needs.</h2>
        </Reveal>
        <Stagger className="feature-grid feature-grid--suite">
          {siteHighlights.map((highlight, index) => {
            const Icon = iconMap[index] ?? Building2;
            return (
              <StaggerItem className="feature" key={highlight.title}>
                <span className="feature__icon">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <h3 className="feature__title">{highlight.title}</h3>
                <p className="feature__body">{highlight.body}</p>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>
      <HeroParallax
        className="angle-feature"
        image="/assets/local/anglers.jpg"
        overlay="linear-gradient(180deg, rgba(6,15,13,0.74) 0%, rgba(6,15,13,0.36) 40%, rgba(6,15,13,0.78) 100%), radial-gradient(135% 80% at 50% 42%, transparent 36%, rgba(6,15,13,0.5))"
      >
        <Reveal className="angle-feature__panel">
          <p className="eyebrow">The main attraction</p>
          <h2>World-class water, right past the door.</h2>
          <span className="rule-gold angle-feature__rule" aria-hidden="true" />
          <p className="angle-feature__deck serif-italic">
            “Some of the finest wild-trout water in the Northeast runs cold and clear past Deposit.”
          </p>
          <dl className="spec-ledger spec-ledger--center" aria-label="Fly fishing highlights">
            <div>
              <dt>East Branch</dt>
              <dd>of the Delaware River</dd>
            </div>
            <div>
              <dt>Wild trout</dt>
              <dd>Brown &amp; rainbow</dd>
            </div>
            <div>
              <dt>Walk &amp; wade</dt>
              <dd>Easy river access</dd>
            </div>
          </dl>
          <div className="angle-feature__actions">
            <Link className="button button--primary" to="/local-guide">
              Plan your fishing <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </HeroParallax>
      <AnimatePresence>
        {catalogueOpen && (
          <m.div
            className="catalogue-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="1916 Deposit Seed Co. catalogue image"
            onClick={() => setCatalogueOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <m.div
              className="catalogue-frame"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.32, ease: easeOut }}
            >
              <button className="catalogue-close icon-button" type="button" onClick={() => setCatalogueOpen(false)} aria-label="Close catalogue image">
                <X size={22} aria-hidden="true" />
              </button>
              <div
                className="catalogue-zoom-surface"
                onPointerEnter={updateCatalogueMagnifier}
                onPointerMove={updateCatalogueMagnifier}
                onPointerLeave={hideCatalogueMagnifier}
              >
                <img className="catalogue-lightbox-image" src={catalogueImage} alt="1916 Deposit Seed Co. seed catalogue cover" />
                <span className="catalogue-magnifier" aria-hidden="true" />
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
